import React from 'react';
import { useIntl } from 'react-intl';
import { Typography, Box, Divider, Grid } from '@mui/material';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import { getForcedMessages } from '../../../../../i18n';
import Information from '../../../Components/Information';
import capitalize from '../../../../../Helpers/StringsFormatting/capitalize';
import { culture, options } from '../../../../../Helpers/dates';

const useStyles = () => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
  },
  infoStyle: {
    root: {
      flex: 'unset',
    },
    valueStyle: {
      fontWeight: 500,
    },
    captionStyle: {
      fontWeight: 400,
    },
  },
  boolStyle: {
    fontSize: '0.95rem',
    letterSpacing: '0.02em',
    color: 'inherit',
  },
});

const normalizeNumberValue = (value) => {
  const rawValue = `${value ?? ''}`.trim();

  if (!rawValue) return Number.NaN;

  if (rawValue.includes(',')) {
    return Number.parseFloat(rawValue.replace(/\./g, '').replace(',', '.'));
  }

  return Number.parseFloat(rawValue);
};

const numberFormat = new Intl.NumberFormat('it-IT', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: 'always',
});

const formatNumberValue = (value, { prefix = '€ ', suffix = '' } = {}) => {
  const numericValue = normalizeNumberValue(value);

  return `${prefix}${numberFormat.format(
    Number.isNaN(numericValue) ? 0 : numericValue
  )}${suffix}`;
};

const PracticeDetailsPage = function PracticeDetailsPage({
  formData: practiceData = {}, //useForm processes data, if data is inserted gets the inserted otherwise the practice data from backend processed by the hook
}) {
  const intl = useIntl();
  const italianTerms = getForcedMessages('it');
  const classes = useStyles();

  const formatInfo = (info) => {
    //if find a key in intl files get the translated value
    const itKeyName = Object.keys(italianTerms).find(
      (keyName) => italianTerms[keyName].toUpperCase() === info?.toUpperCase()
    );
    if (itKeyName) {
      return intl.messages[itKeyName];
    }
    //else it returns the capitalized value
    return capitalize(info);
  };

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString(culture, options)
      : intl.messages.UNAVAILABLE;

  const hasCompany =
    practiceData?.company1 || practiceData?.company2 || practiceData?.company3;

  // Rows are [caption, value, xs column span].
  const renderRows = (rows) =>
    rows.map(([caption, value, xs = 6]) => (
      <Grid key={caption} size={{ md: 3, sm: 4, xs }}>
        <Information
          styles={classes.infoStyle}
          caption={caption}
          value={value}
        />
      </Grid>
    ));

  return (
    <div style={classes.root}>
      <Grid container rowSpacing="16px" columnSpacing="18px">
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6">{intl.messages.SUMMARY}</Typography>
        </Grid>
        {renderRows([
          [
            intl.messages.CUSTOMER,
            `${formatInfo(practiceData.customerName)} ${formatInfo(
              practiceData.customerSurname
            )}`,
          ],
          [intl.messages.FISCALCODE, practiceData.fiscalCode],
          [
            intl.messages.GENDER,
            practiceData.gender === 'M'
              ? intl.messages.MAN
              : intl.messages.WOMAN,
          ],
          [intl.messages.AGE, practiceData.age],
          [intl.messages.NATIONALITY, formatInfo(practiceData.citizen)],
          [intl.messages.OCCUPATION, formatInfo(practiceData.occupation)],
          [intl.messages.COMPANY, formatInfo(practiceData.company)],
          [intl.messages.CITY, formatInfo(practiceData.city)],
          [intl.messages.ZIPCODE, practiceData.zipCode],
          [intl.messages.PROVINCE, practiceData.province],
        ])}
        <Grid size={{ md: 3, sm: 4, xs: 6 }} />
        <Grid
          size={{ md: 3, sm: 4, xs: 6 }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          {hasCompany && (
            <Box
              sx={{
                display: 'flex',
                gap: '4px',
                color: '#6a6a6a',
                overflow: 'hidden',
              }}
            >
              <DoneRoundedIcon sx={{ color: 'inherit' }} />
              <Typography sx={classes.boolStyle} noWrap>
                {intl.messages.ELIGIBLE}
              </Typography>
            </Box>
          )}
        </Grid>
        {hasCompany &&
          renderRows(
            [1, 2, 3].map((n) => [
              intl.messages[`COMPANY${n}`],
              practiceData?.[`company${n}`] &&
                `${practiceData?.[`company${n}`]} - ${practiceData?.[`rating${n}`]}`,
            ])
          )}

        <Grid size={{ xs: 12 }}>
          <Divider />
        </Grid>

        {renderRows([
          [intl.messages.PRACTICENUMBER, practiceData.practiceNumber],
          [
            intl.messages.DATELOADINGPRACTICE,
            formatDate(practiceData.dateLoadingPractice),
          ],
          [
            intl.messages.PRATICALMANAGEMENT,
            formatInfo(practiceData.praticalManagement),
            12,
          ],
          [intl.messages.LEADSOURCE, formatInfo(practiceData.leadSource)],
          [intl.messages.LOANPURPOSE, formatInfo(practiceData.loanPurpose)],
          [intl.messages.PRODUCTSOURCE, formatInfo(practiceData.productSource)],
          [intl.messages.PRODUCTCODE, formatInfo(practiceData.productCode)],
          [intl.messages.PRACTICESTATE, formatInfo(practiceData.practiceState)],
          [
            intl.messages.LIQUIDATIONDATE,
            formatDate(practiceData.liquidationDate),
          ],
        ])}

        <Grid size={{ xs: 12 }}>
          <Divider />
        </Grid>
        {renderRows([
          [
            intl.messages.DURATION,
            practiceData.duration?.toString() || intl.messages.UNAVAILABLE,
          ],
          [
            intl.messages.REQUIREDAMOUNT,
            formatNumberValue(practiceData?.requiredAmount),
          ],
          [intl.messages.NETAMOUNT, formatNumberValue(practiceData?.netAmount)],
          [intl.messages.STAMP, formatNumberValue(practiceData?.stamp)],
          [
            intl.messages.RATE,
            formatNumberValue(practiceData?.rate, {
              prefix: '',
              suffix: ' %',
            }),
          ],
          [
            intl.messages.ARRANGEMENTFEES,
            formatNumberValue(practiceData?.arrangementFees),
          ],
          [
            intl.messages.EXTERNALDEBTCONSOLIDATION,
            formatNumberValue(practiceData?.externalDebtConsolidation),
          ],
          [
            intl.messages.INTERNALDEBTCONSOLIDATION,
            formatNumberValue(practiceData?.internalDebtConsolidation),
          ],
          [
            intl.messages.INSURANCEPREMIUMAMOUNTBUNDLE,
            formatNumberValue(practiceData?.insurancePremiumAmountBundle),
          ],
          [
            intl.messages.INSURANCEPREMIUMAMOUNTCPI,
            formatNumberValue(practiceData?.insurancePremiumAmountCPI),
          ],
          [intl.messages.IDENTINET, formatNumberValue(practiceData?.identinet)],
        ])}

        <Grid size={{ xs: 12 }}>
          <Information
            caption={intl.messages.NOTES}
            value={practiceData.notes}
            noWrap={false}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default PracticeDetailsPage;
